/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { DayOfWeek, ServiceDate } from 'types/entities/shared/ids'

import type { DailyStopApi } from 'types/api/route/dailyStop'
import type { MasterStopApi } from 'types/api/route/masterStop'

/**
 * Holds the master-route board's pending preview state. Two mutually exclusive
 * sources can stage a preview: the master-column sync popover (`source: 'sync'`)
 * or the version-history restore dialog (`source: 'restore'`). Either way, the
 * Board view consumes this slice to render preview chips on affected daily
 * columns and the publish button reads `source` to know which commit operation
 * to fire.
 *
 * The two sources are mutually exclusive at the call-site level: the sync
 * popover and the restore dialog both check {@link selectHasPreview} and refuse
 * to start a new preview when one is already staged. On commit, the matching
 * route operation runs and the slice is cleared.
 */

type PreviewSource = 'sync' | 'restore'

export interface MasterPreviewEntry {
  sequence: number
  stop: MasterStopApi
}

export interface DailyPreviewDate {
  serviceDate: ServiceDate
  // Mutable for the same reason masterPreview is: Immer's WritableDraft
  // requires a mutable inner array. Payloads pass ReadonlyArray and we clone.
  sequence: Array<{ sequence: number; stop: DailyStopApi }>
}

export type RestoreScope = 'master' | 'daily'

export interface RestorePreviewContext {
  versionId: string
  /** Which versions list the restore came from — drives the commit intent. */
  scope: RestoreScope
  syncFuture: boolean
  reassign: boolean
  // Mutable arrays at rest because the slice runs through Immer's
  // WritableDraft. Callers pass ReadonlyArray via the payload type and we
  // clone on assignment.
  masterPreview: MasterPreviewEntry[]
  dailyPreview: DailyPreviewDate[]
}

interface MasterRoutePreviewState {
  source: PreviewSource | null
  dayOfWeek: DayOfWeek | null
  serviceDates: ServiceDate[]
  restoreContext: RestorePreviewContext | null
  // A sync preview staged from the promotion flow — the banner reads this to
  // label it as a fan-out of the just-promoted master order.
  fromPromotion: boolean
}

const initialState: MasterRoutePreviewState = {
  source: null,
  dayOfWeek: null,
  serviceDates: [],
  restoreContext: null,
  fromPromotion: false,
}

export const MASTER_ROUTE_PREVIEW_KEY = 'masterRoutePreview' as const

interface SetSyncPreviewPayload {
  dayOfWeek: DayOfWeek
  serviceDates: ReadonlyArray<ServiceDate>
  // Set when the fan-out was staged from the promotion flow (banner copy differs).
  fromPromotion?: boolean
}

interface SetRestorePreviewPayload {
  dayOfWeek: DayOfWeek
  serviceDates: ReadonlyArray<ServiceDate>
  versionId: string
  scope: RestoreScope
  syncFuture: boolean
  reassign: boolean
  masterPreview: ReadonlyArray<MasterPreviewEntry>
  dailyPreview: ReadonlyArray<DailyPreviewDate>
}

const masterRoutePreviewSlice = createSlice({
  name: MASTER_ROUTE_PREVIEW_KEY,
  initialState,
  reducers: {
    setSyncPreview: (state, action: PayloadAction<SetSyncPreviewPayload>) => {
      state.source = 'sync'
      state.dayOfWeek = action.payload.dayOfWeek
      // Dedupe so a popover bug can't accidentally double-publish the same date.
      state.serviceDates = Array.from(new Set(action.payload.serviceDates))
      state.restoreContext = null
      state.fromPromotion = action.payload.fromPromotion ?? false
    },
    setRestorePreview: (state, action: PayloadAction<SetRestorePreviewPayload>) => {
      state.source = 'restore'
      state.dayOfWeek = action.payload.dayOfWeek
      state.serviceDates = Array.from(new Set(action.payload.serviceDates))
      state.fromPromotion = false
      state.restoreContext = {
        versionId: action.payload.versionId,
        scope: action.payload.scope,
        syncFuture: action.payload.syncFuture,
        reassign: action.payload.reassign,
        masterPreview: action.payload.masterPreview.map(entry => ({ ...entry })),
        // Deep-clone inner `sequence` arrays so the payload's ReadonlyArray
        // can't sneak past the type erasure into the writable slice state.
        dailyPreview: action.payload.dailyPreview.map(day => ({
          serviceDate: day.serviceDate,
          sequence: day.sequence.map(entry => ({ ...entry })),
        })),
      }
    },
    clearPreview: () => initialState,
  },
})

export const { setSyncPreview, setRestorePreview, clearPreview } = masterRoutePreviewSlice.actions

/**
 * Narrow root-state shape — keeps tests free of the full RootState while
 * letting consumers pass through whatever shape their store happens to have.
 */
export interface MasterRoutePreviewRootStateSlice {
  [MASTER_ROUTE_PREVIEW_KEY]?: MasterRoutePreviewState
}

export const selectPreview = (state: MasterRoutePreviewRootStateSlice): MasterRoutePreviewState =>
  state[MASTER_ROUTE_PREVIEW_KEY] ?? initialState

export const selectPreviewSource = (state: MasterRoutePreviewRootStateSlice): PreviewSource | null => selectPreview(state).source

export const selectPreviewDayOfWeek = (state: MasterRoutePreviewRootStateSlice): DayOfWeek | null => selectPreview(state).dayOfWeek

export const selectPreviewServiceDates = (state: MasterRoutePreviewRootStateSlice): ReadonlyArray<ServiceDate> =>
  selectPreview(state).serviceDates

// Restore previews park their payload in `restoreContext` and leave
// `serviceDates` empty — sync previews are the opposite. Branching by source
// keeps both visible to gate consumers (banner, PublishButton fast-path).
export const selectHasPreview = (state: MasterRoutePreviewRootStateSlice): boolean => {
  const preview = selectPreview(state)
  if (preview.source === null) return false
  if (preview.source === 'restore') return preview.restoreContext !== null
  return preview.serviceDates.length > 0
}

export const selectRestorePreviewContext = (state: MasterRoutePreviewRootStateSlice): RestorePreviewContext | null =>
  selectPreview(state).restoreContext

/**
 * True only when both the dayOfWeek matches AND the date is in the previewed
 * set — prevents a stale preview from leaking onto the wrong column when the
 * user changes the active day before committing.
 */
export const selectIsServiceDateInPreview = (
  state: MasterRoutePreviewRootStateSlice,
  dayOfWeek: DayOfWeek,
  serviceDate: ServiceDate
): boolean => {
  const preview = selectPreview(state)
  if (preview.dayOfWeek !== dayOfWeek) return false
  return preview.serviceDates.includes(serviceDate)
}

export { initialState as masterRoutePreviewInitialState }
export type { PreviewSource, MasterRoutePreviewState }

export default masterRoutePreviewSlice.reducer
